import type { BillingFrequency, PaymentMethod, RenewalAdjustmentType } from '../generated/enums.js';

/**
 * WorkOS org API key scopes for the External API contract-template surface (ALC-2820).
 * Same auth plane as `ExternalContractScope` — checked by `RequireExternalScope` against
 * `req.externalContext.permissions`, never Platform JWT RBAC slugs (see
 * docs/agents/identity-decisions.md § ID-024).
 */
export const ExternalContractTemplateScope = {
  READ: 'external:contract-templates:read',
  WRITE: 'external:contract-templates:write',
} as const;
export type ExternalContractTemplateScope =
  (typeof ExternalContractTemplateScope)[keyof typeof ExternalContractTemplateScope];

/** All External API contract-template scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_CONTRACT_TEMPLATE_SCOPES = [
  ExternalContractTemplateScope.READ,
  ExternalContractTemplateScope.WRITE,
] as const satisfies readonly ExternalContractTemplateScope[];

/**
 * Maximum length of a contract template `code` (`BillingContractTemplate.code`,
 * `@db.VarChar(20)`). Unlike `BillingContract.externalReferenceId`, a template has no
 * separate long-reference column — the caller-supplied `code` IS the persisted column,
 * so an over-length code is rejected at the boundary rather than derived or truncated.
 */
export const EXTERNAL_TEMPLATE_CODE_MAX_LENGTH = 20;

export interface ExternalContractTemplateItemRequest {
  /** Billing service catalog code — resolved to a service in the caller's tenant, same
   * convention as the invoice and contract item surfaces. */
  serviceCode: string;
  /** Default per-person regular price after any introductory period. */
  regularPrice?: number | null;
  /** Fixed introductory price, including zero. */
  introductoryPrice?: number | null;
  /** Monthly billing periods charged at `introductoryPrice`. */
  introductoryMonths?: number | null;
}

/**
 * Template create payload (ALC-2820) — deliberately narrow. CDS loads renewal rules and
 * pricing defaults once as templates, then creates contracts referencing them; anything
 * that shapes HOW revenue is recognized or WHEN it is billed (`defaultBillingTiming`,
 * `defaultServicePeriodRule`, `defaultRecognitionMethod`, `proRataFirstCycle`,
 * `billingDayOfMonth`, `paymentTermsDays`, `boletoInstructionsOverride`,
 * `cancellationNoticeDays`, `currency`, `description`, `isActive`) stays ERP-owned and is
 * refused with `TEMPLATE_FIELD_NOT_WRITABLE` rather than silently ignored — see
 * `assertOnlyWritableFields` in `external-writable-fields.util.ts`.
 */
export interface ExternalCreateContractTemplateRequest {
  entityId: string;
  /** Idempotency key: a template create replays by (tenant, code) — see
   * `ExternalContractTemplateResponse` and the `TEMPLATE_CODE_CONFLICT` error. */
  code: string;
  name: string;
  billingFrequency?: BillingFrequency;
  defaultDurationMonths?: number | null;
  autoRenewal?: boolean;
  renewalDurationMonths?: number | null;
  renewalAdjustmentType?: RenewalAdjustmentType | null;
  renewalAdjustmentValue?: number | null;
  renewalIndex?: string | null;
  renewalAccumulatedVariationWindowMonths?: number | null;
  renewalPublicationLagDays?: number | null;
  renewalFloorAtZero?: boolean;
  paymentMethod?: PaymentMethod | null;
  installmentCount?: number | null;
  installmentIntervalDays?: number | null;
  items?: ExternalContractTemplateItemRequest[];
}

export interface ExternalContractTemplateItemResponse {
  serviceId: string;
  serviceCode: string | null;
  regularPrice: number | null;
  introductoryPrice: number | null;
  introductoryMonths: number | null;
}

export interface ExternalContractTemplateResponse {
  id: string;
  entityId: string;
  code: string;
  name: string;
  billingFrequency: BillingFrequency;
  defaultDurationMonths: number | null;
  autoRenewal: boolean;
  renewalDurationMonths: number | null;
  renewalAdjustmentType: RenewalAdjustmentType | null;
  renewalAdjustmentValue: number | null;
  renewalIndex: string | null;
  renewalAccumulatedVariationWindowMonths: number | null;
  renewalPublicationLagDays: number | null;
  renewalFloorAtZero: boolean;
  paymentMethod: PaymentMethod | null;
  installmentCount: number | null;
  installmentIntervalDays: number | null;
  items: ExternalContractTemplateItemResponse[];
  isActive: boolean;
  createdAt: string;
}

export interface ExternalContractTemplateListResponse {
  templates: ExternalContractTemplateResponse[];
}
